// src/admin/AdminCategories.jsx - Full Screen Modern Design
import React, { useState, useEffect } from 'react';
import { categoriesAPI } from '../../services/api';
import {
  Plus,
  Edit3,
  Trash2,
  Image as ImageIcon,
  Upload,
  X,
  AlertCircle,
  Check,
  Search,
  FolderOpen,
  Layers,
  TrendingUp,
  Save,
  RotateCcw,
  Grid3X3,
  List,
  Tag
} from 'lucide-react';
import './css/AdminCategories.css';

const AdminCategories = () => {
  const [categories, setCategories] = useState([]);
  const [formData, setFormData] = useState({ name: '', description: '' });
  const [imageFile, setImageFile] = useState(null);
  const [imagePreview, setImagePreview] = useState('');
  const [editingId, setEditingId] = useState(null);
  const [showForm, setShowForm] = useState(false);
  const [loading, setLoading] = useState(false);
  const [fetchLoading, setFetchLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [viewMode, setViewMode] = useState('grid');
  const [notification, setNotification] = useState({ show: false, type: '', message: '' });

  useEffect(() => {
    fetchCategories();
  }, []);

  useEffect(() => {
    if (notification.show) {
      const timer = setTimeout(() => setNotification({ show: false, type: '', message: '' }), 4000);
      return () => clearTimeout(timer);
    }
  }, [notification]);

  const showNotification = (type, message) => {
    setNotification({ show: true, type, message });
  };

  const fetchCategories = async () => {
    setFetchLoading(true);
    try {
      const { data } = await categoriesAPI.getAll();
      setCategories(data);
    } catch (err) {
      showNotification('error', 'Failed to fetch categories');
    } finally {
      setFetchLoading(false);
    }
  };

  const handleImageChange = (e) => {
    const file = e.target.files[0];
    if (file) {
      if (!file.type.startsWith('image/')) {
        showNotification('error', 'Please select a valid image file');
        return;
      }
      if (file.size > 5 * 1024 * 1024) {
        showNotification('error', 'Image size should be less than 5MB');
        return;
      }
      setImageFile(file);
      const reader = new FileReader();
      reader.onloadend = () => setImagePreview(reader.result);
      reader.readAsDataURL(file);
    }
  };

  const handleRemoveImage = () => {
    setImageFile(null);
    setImagePreview('');
  };

  const convertToBase64 = (file) => {
    return new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.readAsDataURL(file);
      reader.onload = () => resolve(reader.result.split(',')[1]);
      reader.onerror = error => reject(error);
    });
  };

  const resetForm = () => {
    setFormData({ name: '', description: '' });
    setImageFile(null);
    setImagePreview('');
    setEditingId(null);
    setShowForm(false);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!formData.name.trim()) {
      showNotification('error', 'Category name is required');
      return;
    }
    if (formData.name.trim().length < 2) {
      showNotification('error', 'Category name must be at least 2 characters');
      return;
    }

    setLoading(true);
    try {
      let imageBase64 = null;
      if (imageFile) {
        imageBase64 = await convertToBase64(imageFile);
      }

      const payload = {
        name: formData.name.trim(),
        description: formData.description.trim() || null,
        image: imageBase64
      };

      if (editingId) {
        await categoriesAPI.update(editingId, payload);
        showNotification('success', 'Category updated successfully!');
      } else {
        await categoriesAPI.create(payload);
        showNotification('success', 'Category created successfully!');
      }

      resetForm();
      fetchCategories();
    } catch (err) {
      showNotification('error', err.response?.data?.message || 'Operation failed');
    } finally {
      setLoading(false);
    }
  };

  const handleEdit = (category) => {
    setFormData({ name: category.name, description: category.description || '' });
    setImagePreview(category.imageUrl || '');
    setImageFile(null);
    setEditingId(category.id);
    setShowForm(true);
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Are you sure you want to delete this category?')) return;
    try {
      await categoriesAPI.delete(id);
      showNotification('success', 'Category deleted successfully!');
      fetchCategories();
    } catch (err) {
      showNotification('error', 'Failed to delete category');
    }
  };

  const handleChange = (e) => {
    setFormData({ ...formData, [e.target.name]: e.target.value });
  };

  const filteredCategories = categories.filter(category =>
    category.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
    category.description?.toLowerCase().includes(searchTerm.toLowerCase())
  );

  // Stats
  const totalCategories = categories.length;
  const withImages = categories.filter(c => c.imageUrl).length;
  const withDescriptions = categories.filter(c => c.description).length;

  if (fetchLoading) {
    return (
      <div className="ac-loading">
        <div className="ac-spinner"></div>
        <p>Loading categories...</p>
      </div>
    );
  }

  return (
    <div className="ac-wrapper">
      {/* Notification */}
      {notification.show && (
        <div className={`ac-notification ${notification.type}`}>
          {notification.type === 'success' ? <Check size={18} /> : <AlertCircle size={18} />}
          <span>{notification.message}</span>
          <button onClick={() => setNotification({ show: false, type: '', message: '' })}>
            <X size={16} />
          </button>
        </div>
      )}

      {/* Sidebar */}
      <aside className="ac-sidebar">
        <div className="ac-sidebar-header">
          <h2>Categories</h2>
          <button
            className="ac-btn-add"
            onClick={() => { resetForm(); setShowForm(true); }}
          >
            <Plus size={20} />
          </button>
        </div>

        {/* Stats */}
        <div className="ac-stats">
          <div className="ac-stat-card">
            <div className="ac-stat-icon red">
              <FolderOpen size={20} />
            </div>
            <div className="ac-stat-info">
              <span className="ac-stat-value">{totalCategories}</span>
              <span className="ac-stat-label">Total Categories</span>
            </div>
          </div>
          <div className="ac-stat-card">
            <div className="ac-stat-icon green">
              <ImageIcon size={20} />
            </div>
            <div className="ac-stat-info">
              <span className="ac-stat-value">{withImages}</span>
              <span className="ac-stat-label">With Images</span>
            </div>
          </div>
          <div className="ac-stat-card">
            <div className="ac-stat-icon blue">
              <Layers size={20} />
            </div>
            <div className="ac-stat-info">
              <span className="ac-stat-value">{withDescriptions}</span>
              <span className="ac-stat-label">With Descriptions</span>
            </div>
          </div>
        </div>

        {/* Quick Filter */}
        <div className="ac-filter-section">
          <h3>Quick Filters</h3>
          <div className="ac-filter-list">
            <button className="ac-filter-item active">
              <span>All Categories</span>
              <span className="ac-filter-count">{categories.length}</span>
            </button>
            <button className="ac-filter-item">
              <span>With Images</span>
              <span className="ac-filter-count">{withImages}</span>
            </button>
            <button className="ac-filter-item">
              <span>No Images</span>
              <span className="ac-filter-count">{totalCategories - withImages}</span>
            </button>
          </div>
        </div>
      </aside>

      {/* Main Content */}
      <main className="ac-main">
        {/* Top Bar */}
        <div className="ac-topbar">
          <div className="ac-search-wrapper">
            <Search size={20} />
            <input
              type="text"
              placeholder="Search categories..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
            />
            {searchTerm && (
              <button className="ac-search-clear" onClick={() => setSearchTerm('')}>
                <X size={16} />
              </button>
            )}
          </div>

          <div className="ac-topbar-actions">
            <div className="ac-view-toggle">
              <button
                className={viewMode === 'grid' ? 'active' : ''}
                onClick={() => setViewMode('grid')}
                title="Grid view"
              >
                <Grid3X3 size={18} />
              </button>
              <button
                className={viewMode === 'list' ? 'active' : ''}
                onClick={() => setViewMode('list')}
                title="List view"
              >
                <List size={18} />
              </button>
            </div>

            <button className="ac-btn-primary" onClick={() => { resetForm(); setShowForm(true); }}>
              <Plus size={18} />
              <span>Add Category</span>
            </button>
          </div>
        </div>

        {/* Page Header */}
        <div className="ac-page-header">
          <div>
            <h1>All Categories</h1>
            <p>{filteredCategories.length} categories found</p>
          </div>
        </div>

        {/* Categories Content */}
        <div className="ac-content">
          {filteredCategories.length === 0 ? (
            <div className="ac-empty">
              {searchTerm ? (
                <>
                  <Search size={64} />
                  <h3>No results found</h3>
                  <p>Try different search terms</p>
                  <button className="ac-btn-outline" onClick={() => setSearchTerm('')}>
                    Clear Search
                  </button>
                </>
              ) : (
                <>
                  <FolderOpen size={64} />
                  <h3>No categories yet</h3>
                  <p>Start by creating your first category</p>
                  <button className="ac-btn-primary" onClick={() => { resetForm(); setShowForm(true); }}>
                    <Plus size={18} />
                    Add Category
                  </button>
                </>
              )}
            </div>
          ) : (
            <div className={`ac-categories ac-${viewMode}`}>
              {filteredCategories.map(category => (
                <div key={category.id} className="ac-card">
                  <div className="ac-card-image">
                    {category.imageUrl ? (
                      <img
                        src={category.imageUrl}
                        alt={category.name}
                        onError={(e) => e.target.src = '/placeholder-image.jpg'}
                      />
                    ) : (
                      <div className="ac-card-no-image">
                        <ImageIcon size={40} />
                      </div>
                    )}
                    <div className="ac-card-overlay">
                      <button onClick={() => handleEdit(category)} title="Edit">
                        <Edit3 size={22} />
                      </button>
                      <button onClick={() => handleDelete(category.id)} title="Delete">
                        <Trash2 size={22} />
                      </button>
                    </div>
                  </div>
                  <div className="ac-card-body">
                    <div className="ac-card-icon">
                      <Tag size={16} />
                    </div>
                    <h3 className="ac-card-title">{category.name}</h3>
                    <p className="ac-card-description">
                      {category.description || 'No description added'}
                    </p>
                    <div className="ac-card-meta">
                      {category.imageUrl ? (
                        <span className="ac-meta-badge has-image">
                          <ImageIcon size={12} />
                          Has Image
                        </span>
                      ) : (
                        <span className="ac-meta-badge no-image">
                          No Image
                        </span>
                      )}
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      </main>

      {/* Category Form Modal */}
      {showForm && (
        <div className="ac-modal-overlay" onClick={() => resetForm()}>
          <div className="ac-modal" onClick={(e) => e.stopPropagation()}>
            <div className="ac-modal-header">
              <div>
                <h2>{editingId ? 'Edit Category' : 'New Category'}</h2>
                <p>Fill in the category details below</p>
              </div>
              <button className="ac-modal-close" onClick={resetForm}>
                <X size={24} />
              </button>
            </div>

            <form onSubmit={handleSubmit} className="ac-form">
              <div className="ac-form-content">
                {/* Category Name */}
                <div className="ac-field">
                  <label>Category Name <span className="required">*</span></label>
                  <input
                    type="text"
                    name="name"
                    value={formData.name}
                    onChange={handleChange}
                    placeholder="Enter category name"
                    maxLength={50}
                    required
                  />
                  <div className="ac-field-footer">
                    <small>Give your category a clear, descriptive name</small>
                    <span>{formData.name.length}/50</span>
                  </div>
                </div>

                {/* Description */}
                <div className="ac-field">
                  <label>Description <small>(optional)</small></label>
                  <textarea
                    name="description"
                    value={formData.description}
                    onChange={handleChange}
                    placeholder="Brief description of this category..."
                    rows={4}
                    maxLength={200}
                  />
                  <div className="ac-field-footer">
                    <small>Describe what products belong in this category</small>
                    <span>{formData.description.length}/200</span>
                  </div>
                </div>

                {/* Image Upload */}
                <div className="ac-field">
                  <label>
                    Category Image
                    <small>{editingId ? '(leave blank to keep current)' : '(optional)'}</small>
                  </label>
                  
                  <div className="ac-image-upload">
                    {imagePreview ? (
                      <div className="ac-image-preview">
                        <img src={imagePreview} alt="Preview" />
                        <button type="button" onClick={handleRemoveImage}>
                          <X size={18} />
                        </button>
                        <div className="ac-image-info">
                          <ImageIcon size={16} />
                          <span>Image selected</span>
                        </div>
                      </div>
                    ) : (
                      <label className="ac-upload-zone">
                        <input
                          type="file"
                          accept="image/*"
                          onChange={handleImageChange}
                        />
                        <Upload size={40} />
                        <span>Click to upload image</span>
                        <small>PNG, JPG up to 5MB</small>
                      </label>
                    )}
                  </div>
                </div>
              </div>

              <div className="ac-form-footer">
                <button type="button" className="ac-btn-secondary" onClick={resetForm}>
                  Cancel
                </button>
                <button type="submit" className="ac-btn-primary" disabled={loading}>
                  {loading ? (
                    <>
                      <div className="ac-btn-spinner"></div>
                      Saving...
                    </>
                  ) : (
                    <>
                      <Save size={18} />
                      {editingId ? 'Update Category' : 'Create Category'}
                    </>
                  )}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
};

export default AdminCategories;